import { useRef, useState } from "react";
import api, { fmtEUR, fmtNum } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload } from "lucide-react";
import { toast } from "sonner";

export function AccountsCard({ accounts, onSelectAccount, onChangeKind, onOpenBalance, onImported }) {
  const [importAccount, setImportAccount] = useState("");
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const importFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !importAccount) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/banks/accounts/${importAccount}/import`, fd,
        { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      toast.success(`Importados ${r.data.imported} movimientos · ${r.data.duplicates} duplicados ignorados · ${r.data.errors} con error`);
      onImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <Card data-testid="accounts-card">
      <CardHeader><CardTitle className="text-base font-semibold font-heading">Cuentas bancarias</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {accounts.map((a) => (
            <div key={a.id} data-testid={`account-card-${a.id}`}
              onClick={() => onSelectAccount(a.id)}
              className="border rounded-xl p-4 space-y-1 cursor-pointer hover:border-[#D9381E] hover:shadow-sm transition-colors">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{a.name}</span>
                <Badge variant="outline" className="text-xs">{a.branch_name}</Badge>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-slate-400 uppercase tracking-wider">Tipo:</span>
                <Select value={a.kind || "cuenta"} onValueChange={(v) => onChangeKind(a.id, v)}>
                  <SelectTrigger data-testid={`account-kind-${a.id}`} className="h-7 w-36 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cuenta">Cuenta bancaria</SelectItem>
                    <SelectItem value="tarjeta">Tarjeta</SelectItem>
                    <SelectItem value="efectivo">Efectivo / caja</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="text-xs text-slate-500 font-num">{a.iban || a.bank || ""}</div>
              <div className="flex items-end justify-between pt-1">
                <span className="font-num font-bold text-lg">{a.current_balance !== null && a.current_balance !== undefined ? fmtEUR(a.current_balance) : "—"}</span>
                <span className="text-xs text-slate-400">{fmtNum(a.tx_count)} movimientos</span>
              </div>
              <button data-testid={`account-initial-balance-${a.id}`}
                onClick={(e) => { e.stopPropagation(); onOpenBalance(a); }}
                className="text-[11px] text-slate-400 hover:text-[#D9381E] text-left transition-colors">
                {a.initial_balance !== null && a.initial_balance !== undefined
                  ? `Saldo inicial: ${fmtEUR(a.initial_balance)} · ${a.initial_balance_date || "—"}`
                  : "+ Fijar saldo inicial"}
              </button>
            </div>
          ))}
          {accounts.length === 0 && (
            <div className="text-sm text-slate-400 py-4 col-span-3">Crea tu primera cuenta bancaria para importar extractos.</div>
          )}
        </div>
        <div className="flex items-end gap-2 border-t pt-4 flex-wrap">
          <div className="space-y-1.5">
            <Label className="text-xs">Importar extracto a</Label>
            <Select value={importAccount} onValueChange={setImportAccount}>
              <SelectTrigger data-testid="import-account-select" className="w-64 h-9"><SelectValue placeholder="Selecciona cuenta..." /></SelectTrigger>
              <SelectContent>
                {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={importFile} data-testid="bank-file-input" />
          <Button data-testid="import-statement-button" variant="outline" disabled={!importAccount || importing}
            onClick={() => fileRef.current?.click()} className="gap-2">
            <Upload className="w-4 h-4" /> {importing ? "Importando..." : "Importar extracto"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
